"use client";

import { TooltipProvider } from "@personalplanering/ui/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import type { ReactNode } from "react";
import { getQueryClient } from "@/lib/queryClient";

// Root client provider tree — the stable, app-wide layer that never remounts on navigation:
// ThemeProvider (next-themes class strategy — drives the `.dark` token set), the single app-wide
// TooltipProvider (shadcn Tooltip throws without an ancestor), and the ONE app-wide QueryClient
// (lib/queryClient.ts). Every surface — tj/ya AND hr — reads this one client. The per-kind SSE
// collections + driver mount below in SyncProvider (app/tj|ya/layout.tsx) and share this client;
// they are the only per-kind part, not the client (see lib/queryClient.ts).
//
// No SessionProvider: nothing reads `useSession()` — the session is resolved server-side (auth())
// and passed down as props, and `signOut()` from next-auth/react needs no provider. Mounting it
// would add a client-side GET /api/auth/session on load + refetch-on-focus that feeds nothing;
// live session-expiry is surfaced by the SSE driver's `unauthorized` state, not by this provider.
export function Providers({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={getQueryClient()}>
        <TooltipProvider delayDuration={150} skipDelayDuration={300}>
          {children}
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
